import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice.ts";
import React, {
  CSSProperties,
  memo,
  ReactNode,
  useContext,
  useMemo,
} from "react";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context.tsx";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon.tsx";
import { LanguageNameSpaces, PrecisionType } from "@shared/types";
import styled, { css, useTheme } from "styled-components";
import { MOBILE_LAYOUT_ONE_COLUMN } from "common/Layouts/TwoChartsTableLayout.tsx";
import Text from "common/Typography/Text.tsx";
import { hexToRGB } from "@shared/utils/misc.ts";
import useMedia from "utils/hooks/useMedia.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";

const useClosedTradesStatistics = () => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getTradesQuery, precisionConfig, inDrawer } =
    useContext(TradesPageContext)!;
  const aggregatedData = getTradesQuery?.data?.aggregated_data;
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_LAYOUT_ONE_COLUMN})`],
    [true],
    false
  );

  const closedTradesStatistic = (
    <Statistic
      name={t("statistics.closed_trades")}
      value={aggregatedData?.number_of_closed_trades || 0}
    />
  );

  const iconStyle = useMemo(() => {
    const style: CSSProperties = isMobile
      ? {
          fontSize: theme.typography.fontSizes.L,
          marginRight: 2,
        }
      : {};
    if (isMobile && !inDrawer) {
      style.color = theme.isNewLook
        ? theme.colors.secondaryText
        : hexToRGB(theme.colors.white, 0.5);
    }
    return style;
  }, [theme, isMobile, inDrawer]);

  const change = aggregatedData?.change[displayCurrency] || 0;
  const changeValue = (
    <TextWithPrecisionAndIcon
      text={change}
      precisionConfig={precisionConfig}
      precisionType={PrecisionType.BALANCE}
      iconStyle={iconStyle}
    />
  );
  const totalChangeStatistic = (
    <Statistic
      name={t("statistics.total_change")}
      value={
        theme.isNewLook ? (
          // The new look colours P&L by direction, the currency included.
          <DirectionValue
            style={{ color: directionColor(getNewLookTokens(theme), change) }}
          >
            {changeValue}
          </DirectionValue>
        ) : (
          changeValue
        )
      }
    />
  );

  return { closedTradesStatistic, totalChangeStatistic };
};

export default useClosedTradesStatistics;

interface StatisticProps {
  name: string;
  value: ReactNode;
}

const Statistic = memo(({ name, value }: StatisticProps) => {
  const { inDrawer } = useContext(TradesPageContext)!;

  const isMobile = useMedia(
    [`(max-width: ${MOBILE_LAYOUT_ONE_COLUMN})`],
    [true],
    false
  );

  return (
    <StatisticWrapper $isMobile={isMobile}>
      <StatisticTitle $isMobile={isMobile} $inDrawer={inDrawer}>
        {name}
      </StatisticTitle>
      <StatisticValue as="div" $isMobile={isMobile} $inDrawer={inDrawer}>
        {value}
      </StatisticValue>
    </StatisticWrapper>
  );
});

const StatisticWrapper = styled.div<{
  $isMobile: boolean;
}>`
  padding: 16px;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
  &:not(:last-of-type) {
    border-bottom: ${(p) => p.theme.borders.light};
  }

  ${(p) =>
    p.$isMobile &&
    `
     align-items: flex-start;
     padding: 0;
     border-bottom: none !important;
     &:not(:last-of-type) {
      border-right: ${p.theme.borders.light};
      width: 100%;
     }
    `};
`;
const DirectionValue = styled.span`
  * {
    color: inherit !important;
  }
`;
const StatisticTitle = styled(Text).attrs({
  type: "secondary",
})<{ $isMobile: boolean; $inDrawer?: boolean }>`
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-size: ${(p) => p.theme.typography.fontSizes.XL};

  ${(p) =>
    p.$isMobile &&
    `
    font-size: ${p.theme.typography.fontSizes.S};
    `};
  ${(p) =>
    p.$isMobile &&
    !p.$inDrawer &&
    `
    color: ${
      p.theme.isNewLook ? p.theme.colors.titleColor : p.theme.colors.white
    };
    `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${newLookType.label};
      color: ${getNewLookTokens(p.theme).secondary};
    `};
`;
const StatisticValue = styled(Text)<{
  $isMobile: boolean;
  $inDrawer?: boolean;
}>`
  line-height: 40px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-size: ${(p) => p.theme.typography.fontSizes.XXL};

  ${(p) =>
    p.$isMobile &&
    `
    font-size: 22px;
    line-height: 28px;
   `};
  ${(p) =>
    p.$isMobile &&
    !p.$inDrawer &&
    `
    color: ${
      p.theme.isNewLook ? p.theme.colors.titleColor : p.theme.colors.white
    };
    `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 4px;
      ${newLookType.figureLarge};
      color: ${getNewLookTokens(p.theme).ink};
      * {
        font-size: inherit !important;
        font-weight: inherit !important;
      }
    `};
`;
